import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LucideIcon } from 'lucide-react-native';
import { ReactNode, memo, useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { BiSurfaceCard } from '../../components/ui/BiSurfaceCard';

/**
 * Below this card width the title cannot share a row with the icon and the
 * actions (a 3-column card on an iPad in portrait), so the actions move to
 * a row of their own above a title of up to two lines.
 */
const kNarrowCardPx = 260;

/** The card's side margin, which its body keeps at the bottom too. */
const kCardPadding = 14;

/**
 * A dashboard card's chrome at a height its layout gives it, or, without
 * one, as tall as its content (a KPI group fits its tiles).
 */
export const CardFrame = memo(
  ({
    icon: Icon,
    title,
    titleHidden,
    titleCentered,
    onTitlePress,
    subtitle,
    badge,
    trailing,
    footer,
    height,
    compact,
    onFitHeight,
    children,
  }: {
    icon: LucideIcon;
    title: string;
    /** The card's style hides its title; actions keep their row. */
    titleHidden?: boolean;
    titleCentered?: boolean;
    /** Opens the card's page; the body never navigates. */
    onTitlePress?: () => void;
    subtitle?: string | null;
    badge?: ReactNode;
    /** Actions on the right of the title: menu, drag handle. */
    trailing?: ReactNode;
    /** Under the body, inside the card; the body ends above it. */
    footer?: ReactNode;
    /** Undefined: as tall as the content, filling a taller row. */
    height?: number;
    /** Too short for the subtitle: the body gets the room. */
    compact?: boolean;
    /**
     * Without a height: the card's height as its content needs it, however
     * far a taller row stretches it.
     */
    onFitHeight?: (px: number) => void;
    children: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const [width, setWidth] = useState(0);
    // The parts a fitting card's own height adds up from: the header, the
    // body's content (not the body, which a taller row stretches), the
    // footer. The border is 1 above and 1 below.
    const [parts, setParts] = useState({ header: 0, content: 0, footer: 0 });
    const part = (key: keyof typeof parts) => (px: number) =>
      setParts(current =>
        current[key] === px ? current : { ...current, [key]: px },
      );
    const bodyBottom = rpx(footer ? 4 : kCardPadding);
    const fits = height === undefined;
    useEffect(() => {
      if (!fits || !onFitHeight || !parts.header || !parts.content) return;
      // A footer measured before and gone since adds nothing.
      const below = footer ? parts.footer : 0;
      onFitHeight(parts.header + parts.content + bodyBottom + below + 2);
    }, [fits, onFitHeight, parts, bodyBottom, footer]);
    const narrow = width > 0 && width < kNarrowCardPx;
    const icon = (
      <View
        style={{
          width: rpx(28),
          height: rpx(28),
          borderRadius: rpx(8),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tint(aiColors.amber, 0.14),
        }}
      >
        <Icon size={15} color={aiColors.amber} />
      </View>
    );
    const heading = (
      <View style={{ flex: 1, gap: rpx(1) }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: narrow ? 'wrap' : 'nowrap',
            justifyContent: titleCentered ? 'center' : 'flex-start',
            gap: rpx(6),
          }}
        >
          {titleHidden ? null : (
            <Pressable
              accessibilityRole={onTitlePress ? 'link' : 'text'}
              accessibilityLabel={onTitlePress ? `查看详情 ${title}` : title}
              disabled={!onTitlePress}
              onPress={onTitlePress}
              style={{ flexShrink: 1 }}
            >
              <Typography.Text
                medium
                size={rpx(14)}
                color={aiColors.foreground}
                numberOfLines={narrow ? 2 : 1}
              >
                {title}
              </Typography.Text>
            </Pressable>
          )}
          {badge}
        </View>
        {subtitle && !compact && !narrow ? (
          <Typography.Text
            size={rpx(11)}
            color={aiColors.mutedForeground}
            numberOfLines={1}
            style={titleCentered ? { textAlign: 'center' } : undefined}
          >
            {subtitle}
          </Typography.Text>
        ) : null}
      </View>
    );
    return (
      <BiSurfaceCard
        style={{
          height,
          // Fitting its content, it still fills a row stretched by a taller
          // neighbour, so cards side by side stay level.
          flexGrow: height === undefined ? 1 : undefined,
          overflow: 'hidden',
        }}
      >
        <View
          onLayout={event => {
            setWidth(event.nativeEvent.layout.width);
            part('header')(event.nativeEvent.layout.height);
          }}
          style={{
            paddingHorizontal: rpx(kCardPadding),
            paddingTop: rpx(compact ? 8 : 12),
            paddingBottom: rpx(compact ? 8 : 16),
            gap: rpx(4),
          }}
        >
          {narrow && trailing ? (
            <>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {icon}
                {trailing}
              </View>
              <View style={{ flexDirection: 'row' }}>{heading}</View>
            </>
          ) : (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(10),
              }}
            >
              {narrow || titleHidden ? null : icon}
              {heading}
              {trailing}
            </View>
          )}
        </View>
        {/* The padding sits outside the clip, so whatever the body draws
            stops short of the card's edge by the same margin all round. */}
        <View
          style={{
            flexGrow: 1,
            flexShrink: height === undefined ? 0 : 1,
            flexBasis: height === undefined ? 'auto' : 0,
            paddingHorizontal: rpx(kCardPadding),
            paddingBottom: bodyBottom,
          }}
        >
          <View
            style={
              fits
                ? { flexGrow: 1, justifyContent: 'center' }
                : { flex: 1, overflow: 'hidden' }
            }
          >
            {fits ? (
              <View
                onLayout={event =>
                  part('content')(event.nativeEvent.layout.height)
                }
              >
                {children}
              </View>
            ) : (
              children
            )}
          </View>
        </View>
        {footer && fits ? (
          <View
            onLayout={event => part('footer')(event.nativeEvent.layout.height)}
          >
            {footer}
          </View>
        ) : (
          footer
        )}
      </BiSurfaceCard>
    );
  },
);
